import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal } from "./Reveal";
import { useSection } from "../../lib/content";
import { getCtaSource } from "../../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", company: "", phone: "", designation: "", city: "", notes: "", consent_phone: false, consent_email: false };

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#024AD8] focus:ring-4 focus:ring-[#024AD8]/15";

export const LeadForm = ({ selectedDevices = [] }) => {
  const c = useSection("leadForm");
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!selectedDevices.length) return;
    const label = selectedDevices.length > 1 ? "Current Gen 1 devices" : "Current Gen 1 device";
    setForm((f) => ({
      ...f,
      notes: f.notes === "" || f.notes.startsWith("Current Gen 1 device")
        ? `${label}: ${selectedDevices.join(", ")}`
        : f.notes,
    }));
  }, [selectedDevices]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const updateCheck = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/migration-inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        designation: form.designation.trim(),
        city: form.city.trim(),
        consent_phone: form.consent_phone,
        consent_email: form.consent_email,
        notes: form.notes.trim() || null,
        cta_source: getCtaSource() || "direct",
      });
      setSubmitted(true);
      toast.success("Request received — our team will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const successBody = c.successBody
    .replace("{name}", form.name.split(" ")[0] || "there")
    .replace("{email}", form.email);

  return (
    <section id="contact" className="relative bg-[#1a1a1a] py-28 lg:py-36 overflow-hidden">
      <div className="absolute -top-40 right-0 w-[520px] h-[520px] rounded-full bg-[#024AD8]/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5">
          <SectionHead dark chapter="10" eyebrow={c.eyebrow} title={c.title} lead={c.lead} />
          <Reveal delay={0.2}>
            <ul className="mt-10 space-y-4">
              {c.checklist.map((item, i) => (
                <li key={item} className="flex items-center gap-4">
                  <span className="w-9 h-9 rounded-full border border-[#6D8FF0]/40 bg-[#6D8FF0]/10 grid place-items-center font-mono text-xs font-bold text-[#6D8FF0]">
                    {i + 1}
                  </span>
                  <span className="text-slate-200 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-10 text-sm text-slate-500 leading-relaxed max-w-md">{c.note}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-white p-8 lg:p-10 shadow-[0_48px_96px_-32px_rgba(0,0,0,0.6)]">
              {submitted ? (
                <div data-testid="lead-form-success-message" className="py-14 text-center">
                  <span className="mx-auto w-16 h-16 rounded-full bg-emerald-100 grid place-items-center">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">
                    {c.successTitle}
                  </h3>
                  <p className="mt-3 text-slate-600 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                    {successBody}
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm(INITIAL); }}
                    className="mt-8 text-sm font-semibold text-[#024AD8] hover:text-[#0238A8] transition-colors"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                      {c.formTitle}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">{c.formSub}</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="lead-name" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Full name *</label>
                      <input id="lead-name" data-testid="lead-form-name-input" required value={form.name} onChange={update("name")} placeholder="Alex Morgan" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="lead-email" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Work email *</label>
                      <input id="lead-email" data-testid="lead-form-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="alex@company.com" className={inputClass} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="lead-company" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Company *</label>
                      <input id="lead-company" data-testid="lead-form-company-input" required value={form.company} onChange={update("company")} placeholder="Acme Corp" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="lead-phone" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Phone Number *</label>
                      <input id="lead-phone" data-testid="lead-form-phone-input" required type="tel" value={form.phone} onChange={update("phone")} placeholder="+91 98765 43210" className={inputClass} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="lead-designation" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Designation *</label>
                      <input id="lead-designation" data-testid="lead-form-designation-input" required value={form.designation} onChange={update("designation")} placeholder="IT Director" className={inputClass} />
                    </div>
                    <div>
                      <label htmlFor="lead-city" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">City *</label>
                      <input id="lead-city" data-testid="lead-form-city-input" required value={form.city} onChange={update("city")} placeholder="Mumbai" className={inputClass} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lead-notes" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Anything else we should know?</label>
                    <textarea id="lead-notes" rows={3} value={form.notes} onChange={update("notes")} placeholder="Current devices, timelines, trade-in questions…" className={`${inputClass} resize-none`} />
                  </div>
                  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5">
                    <label data-testid="consent-phone-label" className="flex items-start gap-3 cursor-pointer">
                      <input
                        data-testid="consent-phone-checkbox"
                        type="checkbox"
                        required
                        checked={form.consent_phone}
                        onChange={updateCheck("consent_phone")}
                        className="mt-0.5 w-4 h-4 accent-[#024AD8] shrink-0"
                      />
                      <span className="text-sm text-slate-600 leading-relaxed">
                        HP may contact me with personalized offers, support updates and event news by phone. <span className="text-[#FF5050]">*</span>
                      </span>
                    </label>
                    <label data-testid="consent-email-label" className="flex items-start gap-3 cursor-pointer">
                      <input
                        data-testid="consent-email-checkbox"
                        type="checkbox"
                        required
                        checked={form.consent_email}
                        onChange={updateCheck("consent_email")}
                        className="mt-0.5 w-4 h-4 accent-[#024AD8] shrink-0"
                      />
                      <span className="text-sm text-slate-600 leading-relaxed">
                        HP may contact me with personalized offers, support updates and event news by email. <span className="text-[#FF5050]">*</span>
                      </span>
                    </label>
                  </div>
                  <button
                    data-testid="lead-form-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="group w-full inline-flex items-center justify-center gap-2.5 bg-[#024AD8] hover:bg-[#0238A8] disabled:opacity-60 text-white font-semibold px-7 py-4 rounded-xl transition-[background-color,transform] duration-300 hover:scale-[1.01] active:scale-[0.99]"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    )}
                    {submitting ? "Sending…" : c.submitLabel}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
